import {
  lensPath,
  map,
  pathOr,
  set,
  compose,
  tryCatch,
  always,
  cond,
  equals,
  T,
  mergeDeepRight,
  partition,
  any,

} from 'ramda';
import { useSessionHook } from 'src/hooks/useSessionHook';
import { GQLCreateEvent } from 'src/queries/eventMutate';
import { isEmptyOrNil, isNotEmpty } from 'src/utilities/hasValue';
import { mkLoanAppFromSessionWorkflowIntent } from '../Bot/botServiceUtils/preSignSaver';
import { LABEL_PREFIX } from 'src/utilities/naming';
import {
  EVENT_PAYLOAD_AUTO_MATCH_NAME,
  EVENT_PAYLOAD_LEAD_NAME,
  EVENT_PAYLOAD_ONBOARD_COBORROWER,
  EVENT_TYPE_REAL_TIME,
  EVENT_TYPE_GRAPHQL,
  EVENT_PAYLOAD_CREDIT_PULL,
} from 'src/features/constants';
import { currentAppSession } from 'src/utilities/storage';
import { mkBatchEventsBridgeData, mkEventsBridgeData } from 'src/services/objects/events';
import {
  createBatchEventBridge,
  createEventBridge,
  createRealTimeEventBridge,
} from 'src/services/events/event';
import { AutoMatchCreate } from './autoMatchCreate';
import { CoBorrowerOnBoard } from './coborrowerOnBoard';
import { CreditPullProvider } from './CreditPullProvider';
import { checkDeactivatedUponOnEventContext } from './deactivatedUpon';

import { APIResourceValueEvaluator } from 'src/features/core/ChatEngine/Configs/apiResourceFields';
import { getStoreBasedOnAuthenticationType } from '../Configs/apiResouces';
import { isString } from 'src/utilities/types';
import { reportException } from 'src/exceptionReporting';

interface IRawEventContext {
  execute: (data: any) => void;
}

interface ILead {
  leadID: any;
  getLoanAppFromSession: () => void;
  generateCustomData: () => void;
}

export class RawEventContext implements IRawEventContext {
  eventContextsRaw: any;
  onCreateEvent: any;
  loanRoleId: any;
  loanAppId: any;
  loanType: any;
  loanRoleAccountId: any;
  userIntents: any;
  loanProducts: any;
  storeClass: any;

  constructor(eventContextsRaw: any, userIntents = [], loanProducts = []) {
    const { loanRoleId, loanAppId, loanType, loanRoleAccountId } =
      currentAppSession();
    this.eventContextsRaw = eventContextsRaw;
    this.loanRoleId = loanRoleId;
    this.loanAppId = loanAppId;
    this.loanType = loanType;
    this.loanRoleAccountId = loanRoleAccountId;
    this.userIntents = userIntents;
    this.loanProducts = loanProducts;
    this.storeClass = getStoreBasedOnAuthenticationType();
  }

  getAction(eventRaw: any) {
    return pathOr('', ['action'], eventRaw);
  }
  getSource(eventRaw: any) {
    return pathOr('', ['source'], eventRaw);
  }

  getLoanApp() {
    const store = new this.storeClass(this.userIntents);
    const loanApp = store.coreLoanSpec()
    return loanApp;
  }

  //This will make the customdata based on mk_payload_for key
  mkCustomData(eventRaw: any) {
    const payloadKey: any = pathOr('', ['mk_payload_for'], eventRaw);

    return cond([
      [
        equals(EVENT_PAYLOAD_LEAD_NAME),
        always(new LeadCreate().generateCustomData()),
      ],
      [
        equals(EVENT_PAYLOAD_AUTO_MATCH_NAME),
        always(new AutoMatchCreate().generateCustomData(eventRaw)),
      ],
      [
        equals(EVENT_PAYLOAD_ONBOARD_COBORROWER),
        always(
          new CoBorrowerOnBoard().generateCustomData(
            this.userIntents,
            this.loanProducts
          )
        ),
      ],

      [
        equals(EVENT_PAYLOAD_CREDIT_PULL),
        always(
          new CreditPullProvider().generateCustomData(
            eventRaw,
            this.getLoanApp()
          )
        ),
      ],
      [T, always(this.getCustomDataFromConfig(eventRaw))],
    ])(payloadKey);
  }


  getCustomDataFromConfig(eventRaw: any) {
    const customData = pathOr({}, ['custom_data'], eventRaw);
    return this.replaceSpecialKeyFields(customData);
  }


  mkStaticData(eventRaw: any) {
    const staticData = pathOr({}, ['staticData'], eventRaw);
    return this.replaceSpecialKeyFields(staticData);
  }
  // This will check the customData exist or not
  checkCustomDataExist(eventRaw: any) {
    const customData = pathOr({}, ['customData'], eventRaw);
    return isNotEmpty(customData);
  }
  /**
   * This method will replace any special key field values  ex: ___borrower_loan_role_id___ will replaced with the primary borrower id
   * TO-DO:
   * currently it support replacing fields with in an object in future we need to support array of objects
   */
  replaceSpecialKeyFields(data: object) {
    const store = new this.storeClass();
    const apiResourceClass = new APIResourceValueEvaluator(store);
    //@ts-ignore
    return map((value: string) => {
      return isString(value) ? apiResourceClass.replace(value) : value;
    }, data);
  }

  mkPayLoadRaw(eventRaw: any) {
    try {
      return Buffer.from(JSON.stringify(this.mkPayLoad(eventRaw))).toString(
        'base64'
      );
    } catch (e) {
      reportException(`<-------rawEventContext-136---------> ${e}`);
      return;
    }
  }
  // This will make the payload based on the customData and staticData
  mkPayLoad(eventRaw: any) {
    const customData = this.mkCustomData(eventRaw);
    const staticData = this.mkStaticData(eventRaw);
    const payload = this.checkCustomDataExist(eventRaw) && isNotEmpty(customData) ?
      { ...customData, ...staticData }
      : isNotEmpty(customData)
        ? { custom_data: customData, ...staticData }
        : staticData;
    return payload;
  }

  mkFinalPayLoad(eventRaw: any) {
    const payLoadRaw = this.mkPayLoadRaw(eventRaw);
    const source = this.getSource(eventRaw);
    const action = this.getAction(eventRaw);
    return compose(
      set(lensPath(['source']), source),
      set(lensPath(['action']), action),
      set(lensPath(['source_provider']), LABEL_PREFIX),
      set(lensPath(['payload_raw']), payLoadRaw)
    )({});
  }

  mkLoanData() {
    const hasLoanNotCreated = any(isEmptyOrNil)([this.loanAppId, this.loanRoleId, this.loanType, this.loanRoleAccountId]);
    hasLoanNotCreated && reportException('<-------Loan App not created--------->')
    return {
      loan_app_id: this.loanAppId,
      loan_app_role_id: this.loanRoleId,
      loan_type: this.loanType,
      lendsmart_account_id: this.loanRoleAccountId,
    };
  }
  //this will make the payload for realtimeevent and normal event context
  mkEventContextPayload(eventRaw: any) {
    //here we are merging the loanData with payload any same key values present in mkLoanData will be overided from mkPayload
    return mergeDeepRight(this.mkLoanData(), this.mkPayLoad(eventRaw));
  }

  //for running event with graphql
  rawEventExecuter(eventRaw: any) {
    const payLoad = this.mkFinalPayLoad(eventRaw);
    return tryCatch(GQLCreateEvent, (err: any, value: any) => ({
      error: err,
      value,
    }))(payLoad);
  }

  //for running event context in realtime
  realTimeEventBridgeExecutor(eventRaw: any) {
    return this.eventSingleExecutor(eventRaw, createRealTimeEventBridge);
  }
  //for sending event via api
  apiEventBrideExecutor(eventRaw: any) {
    return this.eventSingleExecutor(eventRaw, createEventBridge);
  }


  //This method will execute event payload based on the context_type(realtime,api)
  async eventSingleExecutor(eventRaw: any, eventMethod: any) {
    const payload = this.mkSingleEventBridgeData(eventRaw)
    return await eventMethod(payload);
  }

  mkSingleEventBridgeData(eventRaw: any) {
    const action = this.getAction(eventRaw);
    const source = this.getSource(eventRaw);
    return mkEventsBridgeData(
      source,
      action,
      this.mkEventContextPayload(eventRaw)
    );


  }



  //This will return true or false based on de activate upon condition
  shouldRunEvent(eventRaw: any) {
    const { shouldRun } = checkDeactivatedUponOnEventContext(
      eventRaw,
      this.userIntents
    );
    return (!shouldRun || isEmptyOrNil(eventRaw))
  }

  //This method will make the event payload for batch events
  mkBatchEventPayload = (event: any) => {
    const payload = this.mkEventContextPayload(event)
    const source = this.getSource(event);
    const action = this.getAction(event)
    return {
      source,
      action,
      payload
    }
  }

  executeBatchEvent(batchEvents: any) {
    let batchEventPayload: Object[] = [];
    batchEvents.forEach((event: any) => {
      
      if (!this.shouldRunEvent(event)) {
        batchEventPayload.push(
          this.mkBatchEventPayload(event)
        );
      }
    });
    return isNotEmpty(batchEventPayload) && createBatchEventBridge(mkBatchEventsBridgeData(batchEventPayload))
  }



  execute(eventContexts: any) {
    map(async (eventRaw: any) => {

      if (this.shouldRunEvent(eventRaw)) {
        return;
      }
      const eventType = pathOr('', ['context_type'], eventRaw);
      //here we are deciding in which way we are sending event
      return cond([
        [
          equals(EVENT_TYPE_REAL_TIME),
          () => this.realTimeEventBridgeExecutor(eventRaw),
        ],
        [equals(EVENT_TYPE_GRAPHQL), () => this.rawEventExecuter(eventRaw)],
        [T, () => this.apiEventBrideExecutor(eventRaw)],
      ])(eventType);
    }, eventContexts);
  }
}

class LeadCreate implements ILead {
  leadID: any;
  constructor() {
    const { leadID } = useSessionHook();
    this.leadID = leadID;
  }
  getLoanAppFromSession() {
    return mkLoanAppFromSessionWorkflowIntent();
  }
  generateCustomData() {
    return {
      identity_id: this.leadID,
      loan_app: this.getLoanAppFromSession(),
    };
  }
}

export const executeEventContextRunner = async (
  eventContexts: any,
  intents = [],
  loanProducts: any = []
) => {

  if (isNotEmpty(eventContexts)) {
    const rawEventContextRunner = new RawEventContext(
      eventContexts,
      intents,
      loanProducts
    );

    //This will split the event context into two type one with realtime and graphql and another with normal batch api event
    const [realtimeAndGraphqlEvents, batchEvents] = partition(
      eventContext => {
        const contextType = pathOr('', ['context_type'], eventContext)
        return equals(contextType, EVENT_TYPE_REAL_TIME) || equals(contextType, EVENT_TYPE_GRAPHQL)
      },
      eventContexts
    );
    isNotEmpty(realtimeAndGraphqlEvents) && await rawEventContextRunner.execute(realtimeAndGraphqlEvents);
    isNotEmpty(batchEvents) && await rawEventContextRunner.executeBatchEvent(batchEvents)

  }
};
